import { useCallback, useMemo } from 'react';

import useFetch from '../../../hooks/useFetch';
import { getActivities } from '../../../api';
import type { ActivitiesResponse } from '../../../types';
import { convertAPIActivityToActivity, isActivityToday, isActivityOverdue } from '../../Activities/utils/activityUtils';

/**
 * Hook to fetch activities and calculate counts per customer
 * Makes a single request to avoid database load
 * Returns a map of customer_id -> {count, hasOverdue} for today + overdue activities
 */
export const useCustomerActivitiesCounts = () => {
  const fetchAllActivities = useCallback(() => {
    // Single request with max page_size (API will return max 100 items)
    // This covers most urgent overdue activities without overloading the database
    return getActivities({ page_size: 100, done: false });
  }, []);

  const { data: activitiesResponse, isLoading } = useFetch<ActivitiesResponse | null>(
    fetchAllActivities,
    [fetchAllActivities],
  );

  const customerActivitiesCounts = useMemo(() => {
    const counts = new Map<number, {count: number; hasOverdue: boolean}>();

    if (!activitiesResponse?.results) {
      return counts;
    }

    // Convert API activities to UI format and count today + overdue per customer
    activitiesResponse.results.forEach((apiActivity) => {
      const activity = convertAPIActivityToActivity(apiActivity);

      // Only count today + overdue activities that are not completed
      if (!activity.completed && (isActivityToday(activity) || isActivityOverdue(activity))) {
        const customerId = apiActivity.customer;
        if (customerId) {
          const current = counts.get(customerId) || { count: 0, hasOverdue: false };
          counts.set(customerId, {
            count: current.count + 1,
            hasOverdue: current.hasOverdue || isActivityOverdue(activity),
          });
        }
      }
    });

    return counts;
  }, [activitiesResponse]);

  return { customerActivitiesCounts, isLoading };
};
